import { TextField, TextFieldProps } from '@mui/material';
import { JSX, useId, useMemo } from 'react';
import { FieldPath, FieldValues, useController, UseControllerProps, ValidationRule, ValidationValue } from 'react-hook-form';

export type TextFieldControlProps<T extends FieldValues, TName extends FieldPath<T>> = UseControllerProps<T, TName> & TextFieldProps;

const getRuleValue = <T extends ValidationValue>(rule?: ValidationRule<T>): T | undefined =>
  !rule || typeof rule === 'string' || typeof rule === 'number' || typeof rule === 'boolean' || rule instanceof RegExp ? rule : rule.value;

export const TextFieldControl = <T extends FieldValues, TName extends FieldPath<T>>({
  name,
  rules,
  control,
  defaultValue,
  shouldUnregister,
  ...props
}: TextFieldControlProps<T, TName>): JSX.Element => {
  const { field, fieldState } = useController({ name, rules, control, defaultValue, shouldUnregister });

  const generatedId = useId();
  const inputProps = useMemo(
    () => ({
      minLength: getRuleValue(rules?.minLength),
      maxLength: getRuleValue(rules?.maxLength),
      min: getRuleValue(rules?.min),
      max: getRuleValue(rules?.max),
      ...props.slotProps?.htmlInput,
    }),
    [props.slotProps?.htmlInput, rules?.max, rules?.maxLength, rules?.min, rules?.minLength],
  );

  return (
    <TextField
      {...field}
      {...props}
      id={props.id ?? generatedId}
      required={props.required || !!rules?.required}
      slotProps={{
        ...props.slotProps,
        htmlInput: inputProps,
      }}
      error={!!fieldState.error}
      helperText={fieldState.error?.message || props.helperText}
    />
  );
};
